import {
  FIVE_MINUTES_IN_SECONDS,
  formatDistanceToNowAbbr,
  isValidDateGuard,
} from "@/utilities/datetime";
import { useNow } from "@/utilities/sharedClock";
import { differenceInSeconds } from "date-fns";

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Matches formatDistanceStrict's own units (minutes, then hours, then days)
// so we don't re-render more often than the text can actually change.
const roundForDisplay = (secondsToNow: number): number => {
  const abs = Math.abs(secondsToNow);
  if (abs <= FIVE_MINUTES_IN_SECONDS) return secondsToNow;
  if (abs < HOUR) return Math.round(secondsToNow / MINUTE) * MINUTE;
  if (abs < DAY) return Math.round(secondsToNow / HOUR) * HOUR;
  return Math.round(secondsToNow / DAY) * DAY;
};

const EMINENT_SECONDS = 10 * MINUTE;
const SOON_SECONDS = 20 * MINUTE;

// Urgency flags for styling a countdown. Anything already past start counts as eminent.
const getCountdownUrgency = (secondsToNow: number) => ({
  isEminent: secondsToNow <= EMINENT_SECONDS,
  isSoon: secondsToNow > EMINENT_SECONDS && secondsToNow < SOON_SECONDS,
});

const useCountdown = (until: Date | null): [string, number] => {
  // an invalid Date is treated the same as no date at all
  const target = isValidDateGuard(until) ? until : null;

  const secondsToNow = useNow((nowMs) =>
    target ? roundForDisplay(differenceInSeconds(target, new Date(nowMs))) : 0,
  );
  const remaining = formatDistanceToNowAbbr(target, false, secondsToNow);

  return [remaining, secondsToNow];
};

export { useCountdown, getCountdownUrgency };
